import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Table,
  Tag,
  Progress,
  List,
  Avatar,
  Space,
  Button,
  DatePicker,
  Select,
  Divider
} from 'antd';
import {
  CarOutlined,
  TeamOutlined,
  FileTextOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
// import { Line, Column, Pie } from '@ant-design/plots';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface DashboardData {
  stats: {
    totalVehicles: number;
    activeVehicles: number;
    totalDrivers: number;
    activeDrivers: number;
    todayOrders: number;
    monthlyRevenue: number;
    vehicleUtilization: number;
    customerSatisfaction: number;
  };
  recentOrders: Array<{
    id: string;
    type: 'rental' | 'ride';
    customer: string;
    vehicle: string;
    driver?: string;
    amount: number;
    status: 'pending' | 'active' | 'completed' | 'cancelled';
    createdAt: string;
  }>;
  vehicleStatus: Array<{
    type: string;
    available: number;
    inUse: number;
    maintenance: number;
  }>;
  revenueData: Array<{
    date: string;
    rental: number;
    ride: number;
  }>;
}

const FranchiseDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7days');

  useEffect(() => {
    // サンプルデータの読み込み
    setTimeout(() => {
      setData({
        stats: {
          totalVehicles: 45,
          activeVehicles: 38,
          totalDrivers: 52,
          activeDrivers: 41,
          todayOrders: 127,
          monthlyRevenue: 285000,
          vehicleUtilization: 84.4,
          customerSatisfaction: 4.7
        },
        recentOrders: [
          {
            id: 'O20231201001',
            type: 'rental',
            customer: '田中太郎',
            vehicle: 'メルセデスE300',
            amount: 45000,
            status: 'active',
            createdAt: '2023-12-01 14:30'
          },
          {
            id: 'O20231201002',
            type: 'ride',
            customer: '佐藤花子',
            vehicle: 'トヨタヴェルファイア',
            driver: '鈴木さん',
            amount: 8500,
            status: 'completed',
            createdAt: '2023-12-01 14:15'
          },
          {
            id: 'O20231201003',
            type: 'rental',
            customer: '高橋一郎',
            vehicle: 'BMW X5',
            amount: 68000,
            status: 'pending',
            createdAt: '2023-12-01 13:45'
          }
        ],
        vehicleStatus: [
          { type: 'セダン', available: 15, inUse: 8, maintenance: 2 },
          { type: 'SUV', available: 10, inUse: 6, maintenance: 1 },
          { type: 'ビジネスカー', available: 5, inUse: 3, maintenance: 0 }
        ],
        revenueData: [
          { date: '11-25', rental: 1200000, ride: 850000 },
          { date: '11-26', rental: 1500000, ride: 920000 },
          { date: '11-27', rental: 1800000, ride: 1100000 },
          { date: '11-28', rental: 1400000, ride: 880000 },
          { date: '11-29', rental: 1600000, ride: 960000 },
          { date: '11-30', rental: 2000000, ride: 1250000 },
          { date: '12-01', rental: 2200000, ride: 1320000 }
        ]
      });
      setLoading(false);
    }, 1000);
  }, [timeRange]);

  if (loading || !data) {
    return <div>読み込み中...</div>;
  }

  const orderStatusColors = {
    pending: 'orange',
    active: 'blue',
    completed: 'green',
    cancelled: 'red'
  };

  const orderStatusLabels = {
    pending: '処理待ち',
    active: '進行中',
    completed: '完了',
    cancelled: 'キャンセル'
  };

  const orderColumns = [
    {
      title: '注文番号',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: '種別',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'rental' ? 'blue' : 'green'}>
          {type === 'rental' ? 'レンタカー' : '配車'}
        </Tag>
      )
    },
    {
      title: '顧客',
      dataIndex: 'customer',
      key: 'customer'
    },
    {
      title: '車両',
      dataIndex: 'vehicle',
      key: 'vehicle'
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount.toLocaleString()}`
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: keyof typeof orderStatusLabels) => (
        <Tag color={orderStatusColors[status]}>
          {orderStatusLabels[status]}
        </Tag>
      )
    }
  ];

  // チャート設定は一時的にコメントアウト、チャートライブラリの修正を待っています
  // const revenueConfig = {...};
  // const pieConfig = {...};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>ダッシュボード</Title>
        <Space>
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 120 }}
          >
            <Option value="7days">直近7日</Option>
            <Option value="30days">直近30日</Option>
            <Option value="90days">直近90日</Option>
          </Select>
          <RangePicker />
        </Space>
      </div>

      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="車両総数"
              value={data.stats.totalVehicles}
              prefix={<CarOutlined />}
              suffix={
                <span style={{ fontSize: 14, color: '#666' }}>
                  /{data.stats.activeVehicles}オンライン
                </span>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ドライバー総数"
              value={data.stats.totalDrivers}
              prefix={<TeamOutlined />}
              suffix={
                <span style={{ fontSize: 14, color: '#666' }}>
                  /{data.stats.activeDrivers}オンライン
                </span>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="本日の注文"
              value={data.stats.todayOrders}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#3f8600' }}
              suffix={<ArrowUpOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="月対売上高"
              value={data.stats.monthlyRevenue}
              prefix={<DollarOutlined />}
              formatter={(value) => `¥${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card title="車両利用率" style={{ height: 300 }}>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={data.stats.vehicleUtilization}
                format={(percent) => `${percent}%`}
                strokeColor="#52c41a"
              />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">現在の利用率</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card title="顧客満足度" style={{ height: 300 }}>
            <div style={{ textAlign: 'center', paddingTop: 60 }}>
              <Statistic
                value={data.stats.customerSatisfaction}
                precision={1}
                suffix="/ 5.0"
                valueStyle={{ fontSize: 32, color: '#f5222d' }}
              />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">総合評価</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="車両ステータス分布">
            <List
              dataSource={data.vehicleStatus}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={item.type}
                    description={
                      <Space>
                        <Tag color="green">利用可能 {item.available}</Tag>
                        <Tag color="blue">使用中 {item.inUse}</Tag>
                        <Tag color="orange">メンテナンス {item.maintenance}</Tag>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* 売上トレンドチャート */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="売上トレンド">
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa' }}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} type="secondary">売上トレンドチャート</Title>
                <p style={{ color: '#999' }}>チャートコンポーネントを読み込み中...</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 最近の注文 */}
      <Row gutter={16}>
        <Col span={24}>
          <Card 
            title="最近の注文" 
            extra={
              <Button type="link" onClick={() => window.location.href = '/franchise/orders'}>
                全てを表示
              </Button>
            }
          >
            <Table
              columns={orderColumns}
              dataSource={data.recentOrders}
              pagination={false}
              rowKey="id"
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FranchiseDashboard;